/**
 * Contact form: conditional fields + AJAX submit.
 */

declare global {
  interface Window {
    sqrCaptchaInitDone?: boolean;
    isPowCaptchaLoading?: boolean;
    powCaptchaLoad?: () => void;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function reloadPowCaptcha(): void {
  if (typeof window.powCaptchaLoad !== 'function') {
    return;
  }

  if (!window.isPowCaptchaLoading) {
    window.sqrCaptchaInitDone = false;
    window.powCaptchaLoad();
  }
}

function clearFieldErrors(form: HTMLFormElement): void {
  form.querySelectorAll('.wpcf7-not-valid-tip').forEach(el => el.remove());
  form.querySelectorAll('.wpcf7-not-valid').forEach(el => {
    el.classList.remove('wpcf7-not-valid');
    el.setAttribute('aria-invalid', 'false');
  });
}

function showFieldErrors(form: HTMLFormElement, errors: Record<string, string>): void {
  for (const name of Object.keys(errors)) {
    const message = errors[name];
    const wrap = form.querySelector(`[data-name="${name}"]`);
    if (!wrap) continue;

    const control = wrap.querySelector('input, select, textarea');
    control?.classList.add('wpcf7-not-valid');
    control?.setAttribute('aria-invalid', 'true');

    const tip = document.createElement('span');
    tip.className = 'wpcf7-not-valid-tip';
    tip.setAttribute('role', 'alert');
    tip.textContent = message;
    wrap.appendChild(tip);
  }
}

function setFormMessage(form: HTMLFormElement, message: string): void {
  const output = form.querySelector('.wpcf7-response-output');
  if (output) output.textContent = message;
}

/**
 * Submit a form over AJAX. Field errors render inline; recoverable failures
 * (captcha, nonce, mail, network) keep the form visible with a message so the
 * user can retry, rather than swapping in the terminal panel.
 */
async function submitAffilaeForm(
  form: HTMLFormElement,
  onFieldErrors?: (errors: Record<string, string>) => void,
): Promise<void> {
  clearFieldErrors(form);
  setFormMessage(form, '');

  const url = form.dataset.ajaxUrl;
  const action = form.dataset.ajaxAction;
  if (!url || !action) return;

  const body = new FormData(form);
  body.set('action', action);

  form.classList.remove('sent', 'invalid');
  form.classList.add('submitting');

  try {
    const response = await fetch(url, {
      method: 'POST',
      body,
      credentials: 'same-origin',
    });
    const data = await response.json().catch(() => null);

    if (data?.status === 'sent') {
      form.classList.add('sent');
      form.reset();
      (window.dataLayer ??= []).push({
        event: `${form.dataset.affilaeForm}_form_success`,
        lang: form.dataset.lang,
      });
      return;
    }

    if (data?.errors && Object.keys(data.errors).length > 0) {
      showFieldErrors(form, data.errors);
      onFieldErrors?.(data.errors);
      return;
    }

    setFormMessage(form, form.dataset.msgError ?? '');
  } catch {
    setFormMessage(form, form.dataset.msgError ?? '');
  } finally {
    form.classList.remove('submitting');
    reloadPowCaptcha();
  }
}

function initContactForm(): void {
  const form = document.querySelector<HTMLFormElement>('[data-affilae-form="contact"]');
  if (!form) return;

  const statutSelect = form.querySelector<HTMLSelectElement>('[name="statut"]');
  const sectorSelect = form.querySelector<HTMLSelectElement>('[name="sector"]');
  if (!statutSelect) return;

  const toggle = (field: string, show: boolean, required = false): void => {
    const block = form.querySelector<HTMLElement>(`[data-affilae-field="${field}"]`);
    if (!block) return;

    block.style.display = show ? '' : 'none';

    const input = block.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input, select, textarea',
    );
    if (!input) return;

    if (!show) {
      if (input instanceof HTMLInputElement && input.type === 'file') {
        input.value = '';
      }
      input.required = false;
      input.removeAttribute('aria-required');
    } else if (required) {
      input.required = true;
      input.setAttribute('aria-required', 'true');
    }
  };

  const applyRules = (): void => {
    const statut = statutSelect.value;
    const isDiscover = statut === 'advertiser_discover';
    const isCandidate = statut === 'candidate';
    const sector = sectorSelect?.value ?? '';

    toggle('society', isDiscover);
    toggle('url-site', isDiscover, true);
    toggle('sector', isDiscover);
    toggle('platform', isDiscover);
    toggle('autre-field', isDiscover && sector === 'autre');
    toggle('file-portfolio', isCandidate, true);
  };

  statutSelect.addEventListener('change', applyRules);
  sectorSelect?.addEventListener('change', applyRules);
  applyRules();

  form.addEventListener('submit', (event: Event) => {
    event.preventDefault();
    void submitAffilaeForm(form);
  });
}

document.addEventListener('DOMContentLoaded', initContactForm);

export { submitAffilaeForm, clearFieldErrors, showFieldErrors };
